// src/components/AlumniNetworkPage.tsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Textarea } from './ui/textarea.tsx';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar.tsx';
import { Separator } from './ui/separator.tsx';
import { toast } from 'sonner';
import {
  Search,
  MessageCircle,
  MoreHorizontal,
  Calendar,
  Send,
  ImagePlus,
  X,
  UserCircle,
  MapPin,
  Briefcase,
  GraduationCap,
  XCircle,
  Trash2,
  Megaphone,
  Hourglass
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.tsx';
import { ImageWithFallback } from './resim/ImageWithFallback.tsx';

// --- INTERFACES ---
interface User {
  id: number;
  username: string;
  email: string;
  FirstName?: string;
  LastName?: string;
  ProfilePicture?: string | null;
  GraduationYear?: string;
  Department?: string;
  City?: string;
  Company?: string;
  Occupation?: string; 
  Biography?: string;
  LinkedinLink?: string;
  is_staff?: boolean;
  is_active?: boolean;
}

interface PostAuthor {
  id: number;
  FirstName: string;
  LastName: string;
  profile_picture?: string;
  occupation?: string;
  graduation_year?: string;
}

interface PostComment {
  id: number;
  author: PostAuthor;
  content: string;
  created_at: string;
}

interface Post {
  id: number;
  author: PostAuthor;
  content: string;
  image?: string;
  created_at: string;
  comment_count: number;
  comments: PostComment[];
}

interface AppEvent {
  id: number;
  Title: string;
  StartDate: string;
  Location?: string;
  attendees_count?: number; 
}

interface AppAnnouncement {
  id: number;
  Title: string;
  PublishDate: string;
}

export function AlumniNetworkPage() {
  const navigate = useNavigate();
  
  // --- VERİTABANI STATE'LERİ ---
  const [apiUsers, setApiUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(true);

  const [posts, setPosts] = useState<Post[]>([]);
  const [isPostsLoading, setIsPostsLoading] = useState(true);

  const [apiEvents, setApiEvents] = useState<AppEvent[]>([]);
  const [apiAnnouncements, setApiAnnouncements] = useState<AppAnnouncement[]>([]);

  // --- AKILLI ARAMA STATE'İ ---
  const [searchQuery, setSearchQuery] = useState(''); 
  const isSearchActive = searchQuery.trim() !== '';

  // --- YENİ GÖNDERİ STATE'LERİ ---
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImageFile, setNewPostImageFile] = useState<File | null>(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState<string | null>(null);
  const [isPostLoading, setIsPostLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- YORUM (MODAL) STATE'LERİ ---
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);

  // --- KİMLİK VE GERÇEK YETKİ KONTROLÜ ---
  const currentUserId = Number(localStorage.getItem('userId')) || null;
  const currentUserFromDb = apiUsers.find(u => u.id === currentUserId);
  const isStaff = currentUserFromDb?.is_staff === true;

  // --- YARDIMCI FONKSİYONLAR ---
  const getFullImageUrl = (urlPath: string | null | undefined) => {
    if (!urlPath) return undefined;
    if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) return urlPath;
    const baseUrl = api.defaults.baseURL?.replace('/api', '') || 'https://sdal-project-backend.onrender.com';
    return `${baseUrl}${urlPath}`;
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', month: 'short', day: 'numeric', 
      hour: '2-digit', minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('tr-TR', options);
  };

  const getEventDateParts = (dateString: string) => {
    const dateObj = new Date(dateString);
    return {
      day: dateObj.getDate(),
      month: dateObj.toLocaleDateString('tr-TR', { month: 'short' }).toUpperCase()
    };
  };

  // --- AKILLI SAYAÇ ---
  const getRemainingDaysLabel = (startDateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const eventDate = new Date(startDateString);
    eventDate.setHours(0, 0, 0, 0);

    const differenceInTime = eventDate.getTime() - today.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

    if (differenceInDays === 0) {
      return "Bugün!";
    } else if (differenceInDays === 1) {
      return "Yarın!";
    } else if (differenceInDays > 1) {
      return `${differenceInDays} gün kaldı`;
    } else {
      return "Sona erdi";
    }
  };

  // --- VERİLERİ ÇEKME ---
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const usersResponse = await api.get('/users/');
        const validUsers = usersResponse.data.filter((u: User) => u.is_active !== false);
        setApiUsers(validUsers);
        setFilteredUsers(validUsers);

        const postsResponse = await api.get('/posts/');
        setPosts(postsResponse.data);

        const eventsResponse = await api.get('/events/');
        setApiEvents(eventsResponse.data);

        const announcementsResponse = await api.get('/announcements/');
        setApiAnnouncements(announcementsResponse.data);

      } catch (error) {
        console.error("Veriler çekilemedi:", error);
        toast.error("Sayfa yüklenirken bir hata oluştu.");
      } finally {
        setIsUsersLoading(false);
        setIsPostsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // --- TEKİL AKILLI FİLTRELEME ---
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(apiUsers);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    const result = apiUsers.filter(user => {
      const fullName = `${user.FirstName || ''} ${user.LastName || ''}`.toLowerCase();
      const company = (user.Company || '').toLowerCase();
      const occupation = (user.Occupation || '').toLowerCase();
      const city = (user.City || '').toLowerCase();
      const year = (user.GraduationYear || '').toString();
      
      return fullName.includes(lowerQuery) || 
             company.includes(lowerQuery) || 
             occupation.includes(lowerQuery) ||
             city.includes(lowerQuery) ||
             year.includes(lowerQuery);
    });
    
    setFilteredUsers(result);
  }, [searchQuery, apiUsers]);

  const futureEvents = apiEvents
    .filter(event => new Date(event.StartDate) >= new Date())
    .sort((a, b) => new Date(a.StartDate).getTime() - new Date(b.StartDate).getTime());

  // --- GÖNDERİ PAYLAŞMA & SİLME ---
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewPostImageFile(file);
      setNewPostImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) return;
    setIsPostLoading(true);

    const formData = new FormData();
    formData.append('content', newPostContent);
    if (newPostImageFile) {
      formData.append('image', newPostImageFile);
    }

    try {
      const response = await api.post('/posts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPosts([response.data, ...posts]);
      setNewPostContent('');
      setNewPostImageFile(null);
      setNewPostImagePreview(null);
      setShowCreatePost(false);
      toast.success('Gönderiniz başarıyla paylaşıldı!');
    } catch (error) {
      console.error(error);
      toast.error('Gönderi paylaşılırken bir hata oluştu.');
    } finally {
      setIsPostLoading(false);
    }
  };

  const handleDeletePost = async (postId: number) => {
    if (!window.confirm("Bu gönderiyi silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/posts/${postId}/`);
      setPosts(posts.filter(p => p.id !== postId));
      if (selectedPost?.id === postId) setSelectedPost(null);
      toast.success("Gönderi silindi.");
    } catch (error) {
      console.error(error);
      toast.error("Gönderi silinirken hata oluştu.");
    }
  };

  // --- YORUM İŞLEMLERİ ---
  const handleSubmitComment = async (e: React.FormEvent, targetPostId?: number) => {
    e.preventDefault();
    const activePostId = targetPostId || selectedPost?.id;
    if (!activePostId || !newComment.trim()) return;
    
    setIsCommentSubmitting(true);
    try {
      const response = await api.post('/comments/', {
        post: activePostId,
        content: newComment
      });
      
      const updatePostWithComment = (post: Post) => ({
        ...post,
        comments: [...(post.comments || []), response.data],
        comment_count: post.comment_count + 1
      });

      setPosts(posts.map(p => p.id === activePostId ? updatePostWithComment(p) : p));
      
      if (selectedPost?.id === activePostId) {
        setSelectedPost(updatePostWithComment(selectedPost));
      }

      setNewComment('');
    } catch (error) {
      console.error("Yorum atılamadı:", error);
      toast.error("Yorum eklenirken bir hata oluştu.");
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number, targetPostId: number) => {
    if (!window.confirm("Bu yorumu silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/comments/${commentId}/`);
      
      const removeCommentFromPost = (post: Post) => ({
        ...post,
        comments: post.comments.filter(c => c.id !== commentId),
        comment_count: post.comment_count - 1
      });

      setPosts(posts.map(p => p.id === targetPostId ? removeCommentFromPost(p) : p));
      
      if (selectedPost?.id === targetPostId) {
        setSelectedPost(removeCommentFromPost(selectedPost));
      }

      toast.success("Yorum silindi.");
    } catch (error) {
      console.error(error);
      toast.error("Yorum silinirken hata oluştu.");
    }
  };

  return (
    <div className="pt-10 pb-16 bg-gradient-to-b from-transparent via-[#A8322D]/5 to-transparent">
      <div className="max-w-7xl mx-auto px-4 space-y-12">
        
        {/* ==========================================
            BÖLÜM 1: AKILLI ARAMA ÇUBUĞU
            ========================================== */}
        <section className="max-w-3xl mx-auto w-full">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-extrabold text-slate-900 mb-2">Yolu SDAL'dan Geçenlere Buradan Ulaşın</h2>
            <p className="text-slate-500">Ağımızdaki mezunları isim, meslek, yıl veya şehre göre hızlıca arayın.</p>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <Input
              type="text"
              className="pl-12 pr-12 py-6 w-full rounded-full border-slate-200 shadow-sm text-base focus-visible:ring-blue-500 focus-visible:border-transparent transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isSearchActive && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            )}
          </div>
        </section>

        {/* ==========================================
            GÜNCELLEME: YENİLENMİŞ BÜTÜNSEL TIKLANABİLİR MEZUN KARTLARI
            ========================================== */}
        {isSearchActive && (
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-700">Arama Sonuçları</h3>
              <span className="text-sm bg-red-50 text-[#A8322D] font-bold px-3 py-1 rounded-full border border-red-100">
                {filteredUsers.length} Mezun Bulundu
              </span>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300 shadow-sm">
                <Search className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Aradığınız kriterlere uygun mezun bulunamadı.</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto overflow-x-hidden pr-2 pb-2 custom-scrollbar">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUsers.map((user) => {
                      const fullName = user.FirstName && user.LastName ? `${user.FirstName} ${user.LastName}` : user.username;
                      return (
                          /* KARTIN TAMAMI ARTIK TIKLANABİLİR VE PROFILE YÖNLENDİRİYOR */
                          <Card 
                            key={user.id} 
                            onClick={() => navigate(`/profil?id=${user.id}`)}
                            className="hover:shadow-md hover:border-slate-300 transition-all bg-white flex flex-col h-full overflow-hidden border-slate-200/60 cursor-pointer group/usercard"
                          >
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-4">
                                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-slate-100 bg-slate-50">
                                      <ImageWithFallback src={getFullImageUrl(user.ProfilePicture)} alt={fullName} className="w-full h-full object-cover group-hover/usercard:scale-105 transition-transform duration-300" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                      <CardTitle className="truncate text-lg group-hover/usercard:text-[#A8322D] transition-colors">{fullName}</CardTitle>
                                      <CardDescription>
                                      <div className="flex items-center gap-1 text-sm mt-1">
                                          <GraduationCap className="h-3.5 w-3.5 text-slate-400" />
                                          <span className="truncate text-slate-600">{user.Department || 'Bölüm Belirtilmedi'}</span>
                                          {user.GraduationYear && <span className="text-slate-600 font-medium">- {user.GraduationYear}</span>}
                                      </div>
                                      </CardDescription>
                                  </div>
                                </div>
                            </CardHeader>
                            <CardContent className="flex flex-col flex-1 space-y-3 pt-0">
                                <div className="flex items-start gap-2 text-sm pt-3 border-t border-slate-100">
                                    <Briefcase className="h-4 w-4 text-[#A8322D] mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="font-bold text-slate-800 truncate text-[14px]">{user.Occupation || 'Pozisyon Belirtilmemiş'}</div>
                                        <div className="text-slate-500 text-xs truncate mt-0.5">{user.Company || 'Kurum Belirtilmemiş'}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600 pb-2">
                                    <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                                    <span className="text-xs font-medium text-slate-600 truncate">{user.City || 'Şehir Belirtilmemiş'}</span>
                                </div>
                            </CardContent>
                          </Card>
                      );
                  })}
                </div>
              </div>
            )}
            <Separator className="my-10 border-slate-200/80" />
          </section>
        )}

        {/* ==========================================
            BÖLÜM 2: AĞ PAYLAŞIMLARI VE SAĞ SİDEBAR
            ========================================== */}
        <section>
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            
            {/* ANA AKIŞ (GÖNDERİLER) */}
            <main className="lg:col-span-2 space-y-6">
              
              {/* YENİ GÖNDERİ OLUŞTUR */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Card className="border-slate-200/80 shadow-md bg-white rounded-2xl overflow-hidden">
                  <CardContent className="p-4">
                    {!showCreatePost ? (
                      <button onClick={() => setShowCreatePost(true)} className="w-full flex items-center gap-3 text-left group">
                        <Avatar className="h-11 w-11 bg-slate-200 border border-slate-100">
                          <AvatarImage src={getFullImageUrl(currentUserFromDb?.ProfilePicture)} />
                          <AvatarFallback><UserCircle className="h-6 w-6 text-slate-400" /></AvatarFallback>
                        </Avatar>
                        <div className="flex-1 px-5 py-3.5 rounded-full bg-slate-100/80 group-hover:bg-slate-100 transition-colors border border-slate-200/60">
                          <span className="text-slate-500 text-sm font-medium">Mezun ağında bir şeyler paylaş...</span>
                        </div>
                      </button>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-11 w-11 bg-slate-200 border border-slate-100">
                            <AvatarImage src={getFullImageUrl(currentUserFromDb?.ProfilePicture)} />
                            <AvatarFallback><UserCircle className="h-6 w-6 text-slate-400" /></AvatarFallback>
                          </Avatar>
                          <div className="flex-1 relative">
                            <Textarea 
                              maxLength={2000}
                              placeholder="Paylaşmak istediğiniz bir şey var mı?" 
                              value={newPostContent} 
                              onChange={(e) => setNewPostContent(e.target.value)} 
                              className="min-h-[100px] resize-none border-slate-200 bg-slate-50/50 pr-4 rounded-xl" 
                              autoFocus 
                            />
                            <div className="text-[10px] text-slate-400 text-right mt-1.5">{newPostContent.length}/2000</div>
                          </div>
                        </div>
                        {newPostImagePreview && (
                          <div className="relative group ml-14 max-w-[200px]">
                            <img src={newPostImagePreview} alt="Preview" className="w-full h-auto object-cover rounded-lg border border-slate-200 shadow-sm" />
                            <button onClick={() => {setNewPostImageFile(null); setNewPostImagePreview(null)}} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md"><X className="h-4 w-4" /></button>
                          </div>
                        )}
                        <div className="flex items-center justify-between ml-14">
                          <div>
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                            <Button variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50 font-medium" onClick={() => fileInputRef.current?.click()}>
                              <ImagePlus className="h-4 w-4 mr-2" /> Fotoğraf Ekle
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => { setShowCreatePost(false); setNewPostContent(''); setNewPostImageFile(null); setNewPostImagePreview(null); }}>İptal</Button>
                            <Button size="sm" onClick={handleCreatePost} disabled={isPostLoading || !newPostContent.trim()} className="bg-slate-900 text-white rounded-full px-5"><Send className="h-4 w-4 mr-2" /> Paylaş</Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* GÖNDERİ LİSTESİ */}
              {isPostsLoading ? (
                <div className="text-center py-10 text-slate-500">Gönderiler yükleniyor...</div>
              ) : posts.length === 0 ? (
                 <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300 text-slate-500 shadow-sm">
                   <MessageCircle className="h-8 w-8 mx-auto mb-3 text-slate-300" />
                   Henüz paylaşım yapılmamış. İlk gönderiyi sen paylaş!
                 </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {posts.map((post, index) => {
                    const authorFullName = `${post.author.FirstName} ${post.author.LastName}`;
                    return (
                      <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                        <Card className="border-slate-200/80 shadow-sm bg-white hover:shadow-md transition-shadow duration-300 rounded-2xl overflow-hidden">
                          <CardContent className="p-5">
                            
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex gap-3 cursor-pointer group" onClick={() => navigate(`/profil?id=${post.author.id}`)}>
                                <Avatar className="h-12 w-12 border border-slate-100 shrink-0 shadow-sm group-hover:ring-2 group-hover:ring-blue-100 transition-all">
                                  <AvatarImage src={getFullImageUrl(post.author.profile_picture)} alt={authorFullName} />
                                  <AvatarFallback>{post.author.FirstName.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <h4 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight">{authorFullName}</h4>
                                  <p className="text-[13px] text-slate-600 mt-0.5">{post.author.occupation || 'Meslek Belirtilmemiş'}</p>
                                  <p className="text-[11px] text-slate-400 mt-0.5 font-medium">{formatDate(post.created_at)}</p>
                                </div>
                              </div>

                              {(currentUserId === post.author.id || isStaff) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 -mr-2"><MoreHorizontal className="h-4 w-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="rounded-xl">
                                    <DropdownMenuItem className="text-red-600 focus:text-red-600 cursor-pointer" onClick={() => handleDeletePost(post.id)}>
                                      <Trash2 className="h-4 w-4 mr-2" /> Gönderiyi Sil
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                            
                            <p className="text-[15px] text-slate-800 mb-4 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                            
                            {post.image && (
                                <div 
                                  className="mb-4 relative rounded-xl overflow-hidden border border-slate-100 cursor-pointer hover:opacity-95 transition-opacity bg-slate-50"
                                  onClick={() => setSelectedPost(post)}
                                >
                                  <img src={getFullImageUrl(post.image)} alt="Post" className="w-full object-cover max-h-[450px]" />
                                </div>
                            )}

                            <Separator className="bg-slate-100 mb-3" />
                            
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" className="gap-2 text-slate-600 hover:bg-slate-50 font-medium rounded-full" onClick={() => setSelectedPost(post)}>
                                  <MessageCircle className="h-4 w-4" />
                                  <span>Yorum Yap ({post.comment_count})</span>
                                </Button>
                              </div>

                              {/* YORUMLARIN AKIŞTA ÖNİZLEMESİ */}
                              {post.comments && post.comments.length > 0 && (
                                <div className="space-y-3 bg-slate-50/50 p-3.5 rounded-xl border border-slate-100">
                                  {post.comments.slice(0, 2).map(comment => (
                                    <div key={comment.id} className="flex items-start gap-2.5">
                                      <Avatar className="h-8 w-8 border border-slate-200 shrink-0">
                                        <AvatarImage src={getFullImageUrl(comment.author.profile_picture)} />
                                        <AvatarFallback>{comment.author.FirstName.charAt(0)}</AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1 bg-white px-3.5 py-2 rounded-2xl rounded-tl-sm inline-block max-w-full border border-slate-100/50">
                                        <span className="font-bold text-slate-900 mr-2 cursor-pointer hover:underline" onClick={() => navigate(`/profil?id=${comment.author.id}`)}>
                                          {comment.author.FirstName} {comment.author.LastName}
                                        </span>
                                        <span className="text-slate-700">{comment.content}</span>
                                      </div>
                                    </div>
                                  ))}
                                  {post.comments.length > 2 && (
                                    <button 
                                      onClick={() => setSelectedPost(post)} 
                                      className="text-[12px] text-slate-500 font-semibold hover:text-blue-600 pl-1 transition-colors"
                                    >
                                      {post.comments.length - 2} yorumun tümünü gör...
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  })}
                </AnimatePresence>
              )}
            </main>

            {/* SAĞ SİDEBAR (DUYURULAR VE ETKİNLİKLER) - OKUL KIRMISINA UYUMLU RENKLER */}
            <aside className="lg:col-span-1 space-y-6 sticky top-24 self-start">
              
              {/* DUYURULAR */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                <Card className="border-slate-200/80 shadow-md bg-white rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-slate-900 flex items-center gap-2 text-base cursor-pointer hover:text-[#A8322D] transition-colors" onClick={() => navigate('/duyurular')}>
                        <Megaphone className="h-5 w-5 text-[#A8322D]" /> Duyurular
                      </h4>
                    </div>
                    
                    {apiAnnouncements.length === 0 ? (
                      <div className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-lg">Güncel duyuru bulunmamaktadır.</div>
                    ) : (
                      <div className="space-y-4">
                        {apiAnnouncements.slice(0, 4).map((ann) => (
                          <div key={ann.id} className="group cursor-pointer bg-slate-50/50 p-3 rounded-xl hover:bg-red-50/50 transition-colors border border-transparent hover:border-red-100" onClick={() => navigate(`/duyurular/${ann.id}`)}>
                            <h5 className="font-semibold text-sm text-slate-800 group-hover:text-[#A8322D] line-clamp-2 leading-snug transition-colors">{ann.Title}</h5>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* ETKİNLİKLER */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
                <Card className="border-slate-200/80 shadow-md bg-white rounded-2xl">
                  <CardContent className="p-5">
                    <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-base cursor-pointer hover:text-[#A8322D] transition-colors" onClick={() => navigate('/etkinlikler')}>
                      <Calendar className="h-5 w-5 text-[#A8322D]" /> Yaklaşan Etkinlikler
                    </h4>
                    
                    {futureEvents.length === 0 ? (
                      <div className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-lg">Şu an planlanan bir etkinlik yok.</div>
                    ) : (
                      <div className="space-y-3">
                        {futureEvents.slice(0, 4).map((event) => {
                          const dateParts = getEventDateParts(event.StartDate);
                          const remainingDaysLabel = getRemainingDaysLabel(event.StartDate);

                          return (
                            <div 
                              key={event.id} 
                              className="flex gap-4 items-center p-3 rounded-xl bg-slate-50/50 border border-slate-100 hover:bg-slate-50 hover:border-red-100 transition-colors cursor-pointer group" 
                              onClick={() => navigate('/etkinlikler')}
                            >
                              {/* Takvim İkon Alanı */}
                              <div className="bg-white rounded-xl shadow-sm border border-slate-100 w-14 h-14 flex flex-col items-center justify-center flex-shrink-0 group-hover:border-red-200 transition-colors relative">
                                <div className="text-[10px] uppercase font-bold text-[#A8322D] mb-0.5">{dateParts.month}</div>
                                <div className="text-lg font-black text-slate-900 leading-none">{dateParts.day}</div>
                              </div>
                              
                              {/* Etkinlik Detay Alanı */}
                              <div className="flex-1 min-w-0">
                                <h5 className="font-bold text-[13px] text-slate-900 group-hover:text-[#A8322D] transition-colors line-clamp-1 leading-snug">{event.Title}</h5>
                                
                                <div className="flex items-center mt-1.5">
                                  {/* Kalan Gün Sayacı Etiketi */}
                                  <span className={`text-[11px] flex items-center gap-1 font-bold px-1.5 py-0.5 rounded ${
                                    remainingDaysLabel.includes("Bugün") || remainingDaysLabel.includes("Yarın")
                                      ? "bg-red-100 text-red-700 border border-red-200 animate-pulse"
                                      : "bg-red-50 text-[#A8322D] border border-red-100/70"
                                  }`}>
                                    <Hourglass className="h-3 w-3 shrink-0" /> {remainingDaysLabel}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

            </aside>
          </div>
        </section>

      </div>

      {/* ==========================================
          YENİLENMİŞ YORUM MODALI 
          ========================================== */}
      <Dialog open={!!selectedPost} onOpenChange={(open) => !open && setSelectedPost(null)}>
        <DialogContent className={`p-0 overflow-hidden bg-white rounded-2xl ${selectedPost?.image ? 'md:max-w-4xl lg:max-w-5xl md:flex-row h-[85vh]' : 'max-w-2xl flex-col max-h-[85vh]'} flex w-full border-none shadow-2xl`}>
          <DialogTitle className="sr-only">Gönderi Detayı</DialogTitle>
          
          {/* SOL TARAF: FOTOĞRAF */}
          {selectedPost?.image && (
            <div className="w-full md:w-[55%] lg:w-[60%] bg-slate-950 flex items-center justify-center relative min-h-[300px] md:min-h-0">
              <img src={getFullImageUrl(selectedPost.image)} alt="Post" className="w-full h-full object-contain" />
            </div>
          )}

          <div className={`flex flex-col bg-white ${selectedPost?.image ? 'w-full md:w-[45%] lg:w-[40%] border-l border-slate-200' : 'w-full'} h-full flex-shrink-0`}>
            
            {/* Gönderi Sahibi (Sabit Üst Bilgi) */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0 shadow-sm z-10">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border border-slate-100">
                  <AvatarImage src={getFullImageUrl(selectedPost?.author?.profile_picture)} />
                  <AvatarFallback>{selectedPost?.author?.FirstName?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm leading-tight">{selectedPost?.author?.FirstName} {selectedPost?.author?.LastName}</h4>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium">{selectedPost ? formatDate(selectedPost.created_at) : ''}</p>
                </div>
              </div>
            </div>

            {/* Ortadaki Kaydırılabilir Alan */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
              
              {/* Gönderinin Kendi Açıklaması */}
              {selectedPost?.content && (
                <div className="p-5 pb-3">
                  <p className="text-[15px] text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {selectedPost.content}
                  </p>
                </div>
              )}

              <Separator className="bg-slate-100/80 my-1 mx-4" />

              {/* Yorumların Listesi */}
              <div className="p-4 pt-2">
                {(!selectedPost?.comments || selectedPost.comments.length === 0) ? (
                  <div className="text-center py-12 text-slate-400 text-sm flex flex-col items-center justify-center h-full">
                    <MessageCircle className="h-10 w-10 text-slate-200 mb-3" />
                    <p className="font-medium text-slate-500">Henüz yorum yapılmamış.<br/>İlk yorumu sen yap!</p>
                  </div>
                ) : (
                  <div className="space-y-5 mt-2">
                    {selectedPost.comments.map((comment) => (
                      <div key={comment.id} className="flex items-start gap-3.5 group relative w-full">
                        <Avatar className="h-10 w-10 mt-0.5 border border-slate-100 shrink-0 shadow-sm">
                          <AvatarImage src={getFullImageUrl(comment.author.profile_picture)} />
                          <AvatarFallback>{comment.author.FirstName.charAt(0)}</AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0 pr-6">
                          <div className="bg-slate-50 px-4 py-2.5 rounded-2xl rounded-tl-sm inline-block max-w-full border border-slate-100/50">
                            <span className="font-bold text-slate-900 text-[13px] block mb-0.5 cursor-pointer truncate hover:underline" onClick={() => navigate(`/profil?id=${comment.author.id}`)}>
                              {comment.author.FirstName} {comment.author.LastName}
                            </span>
                            <span className="text-[14px] text-slate-700 break-words leading-snug">{comment.content}</span>
                          </div>
                          <div className="text-[11px] text-slate-400 mt-1.5 ml-2 font-medium">
                            {formatDate(comment.created_at)}
                          </div>
                        </div>

                        {/* Yorum Silme İkonu */}
                        {(currentUserId === comment.author.id || isStaff) && (
                          <button 
                            onClick={() => handleDeleteComment(comment.id, selectedPost.id)} 
                            className="absolute right-0 top-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white rounded-full shadow-sm border border-slate-100"
                            title="Yorumu Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Alt Kısım: Yorum Yazma Alanı */}
            <div className="p-4 bg-white border-t border-slate-100 shrink-0 flex flex-col shadow-[0_-4px_15px_-5px_rgba(0,0,0,0.05)] z-10 w-full">
              <form onSubmit={(e) => handleSubmitComment(e)} className="flex items-center gap-3 relative w-full">
                <Avatar className="h-9 w-9 border border-slate-100 shrink-0">
                  <AvatarImage src={getFullImageUrl(currentUserFromDb?.ProfilePicture)} />
                  <AvatarFallback><UserCircle className="h-5 w-5 text-slate-400" /></AvatarFallback>
                </Avatar>
                <Input 
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  maxLength={1000}
                  placeholder="Yorum ekle..."
                  className="w-full rounded-full bg-slate-50 border-slate-200 focus-visible:ring-1 focus-visible:ring-blue-500 pr-12 text-[14px] py-5 transition-all"
                  disabled={isCommentSubmitting}
                />
                <Button 
                  type="submit" 
                  variant="ghost" 
                  size="icon" 
                  className={`absolute right-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full h-8 w-8 transition-opacity ${newComment.trim() ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}
                  disabled={isCommentSubmitting || !newComment.trim()}
                >
                  <Send className="h-4 w-4 ml-0.5" />
                </Button>
              </form>
              <div className="text-[10px] text-slate-400 text-right mt-1.5 mr-2 font-medium">
                {newComment.length}/1000
              </div>
            </div>
            
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}